import { useState, useCallback, useMemo } from 'react'
import { Modal, ModalDialog, ModalClose, Typography, Divider, LinearProgress, Stack, FormControl, FormLabel, Input, Button } from '@mui/joy'
import KeyIcon from '@mui/icons-material/Key'
import LockIcon from '@mui/icons-material/Lock'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'

import { sendCode, signIn } from '../../../api/telegramApi'
import { type TelegramAccount } from '../types'

interface SignInModalProps {
    open: boolean
    accounts: TelegramAccount[]
    initialPhone?: string
    onClose: () => void
    onSuccess: () => Promise<void>
    onError: (error: string) => void
    onMessage: (message: string) => void
}

export function SignInModal({ open, accounts, initialPhone = '', onClose, onSuccess, onError, onMessage }: SignInModalProps): JSX.Element {
    const [step, setStep] = useState<1 | 2>(1)
    const [phone, setPhone] = useState(initialPhone)
    const [code, setCode] = useState('')
    const [password, setPassword] = useState('')
    const [sendingCode, setSendingCode] = useState(false)
    const [signingIn, setSigningIn] = useState(false)

    const isReconnecting = useMemo(() => {
        return phone && accounts.some((a) => a.phone === phone && a.status === 'logged_out')
    }, [phone, accounts])

    const canSendCode = useMemo(() => phone.trim().length > 5 && !sendingCode && !signingIn, [phone, sendingCode, signingIn])
    const canSignIn = useMemo(() => Boolean(phone.trim()) && Boolean(code.trim()) && !signingIn, [phone, code, signingIn])

    const handleSendCode = useCallback(async () => {
        setSendingCode(true)
        try {
            await sendCode(phone)
            onMessage('Code sent. Check your Telegram app.')
            setStep(2)
        } catch (e) {
            onError((e as Error)?.message || 'Failed to send code')
        } finally {
            setSendingCode(false)
        }
    }, [phone, onMessage, onError])

    const handleBackToPhone = useCallback(() => {
        setStep(1)
        setCode('')
        setPassword('')
    }, [])

    const handleSignIn = useCallback(async () => {
        setSigningIn(true)
        try {
            await signIn(phone, code, password || undefined)
            onMessage('Logged in. Session saved on server.')
            onClose()
            await onSuccess()
        } catch (e) {
            onError((e as Error)?.message || 'Failed to sign in')
        } finally {
            setSigningIn(false)
        }
    }, [phone, code, password, onSuccess, onError, onMessage, onClose])

    const handleModalClose = useCallback(() => {
        setStep(1)
        setPhone(initialPhone)
        setCode('')
        setPassword('')
        onClose()
    }, [initialPhone, onClose])

    return (
        <Modal open={open} onClose={handleModalClose}>
            <ModalDialog variant="outlined" sx={{ borderRadius: 'lg', minWidth: 520 }}>
                <ModalClose />
                <Typography level="h4" component="h5">
                    {isReconnecting ? 'Reconnect to Telegram' : 'Sign in to Telegram'}
                </Typography>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                    {step === 1
                        ? isReconnecting
                            ? `Reconnecting ${phone}. We'll send a login code to your Telegram app.`
                            : "We'll send a login code to your Telegram app."
                        : `Code sent to ${phone}`}
                </Typography>
                <Divider sx={{ my: 1 }} />

                {(sendingCode || signingIn) && <LinearProgress sx={{ mb: 1 }} />}

                {step === 1 && (
                    <Stack spacing={1.5}>
                        <FormControl required>
                            <FormLabel>Phone number</FormLabel>
                            <Input
                                startDecorator={<PhoneIphoneIcon />}
                                placeholder="+1 555 123 4567"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                type="tel"
                                autoComplete="tel"
                                disabled={isReconnecting || undefined}
                                readOnly={isReconnecting || undefined}
                            />
                            {isReconnecting && (
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                                    Reconnecting existing account
                                </Typography>
                            )}
                        </FormControl>
                        <Button variant="solid" onClick={handleSendCode} disabled={!canSendCode} loading={sendingCode} fullWidth>
                            Send code
                        </Button>
                    </Stack>
                )}

                {step === 2 && (
                    <Stack spacing={1.5}>
                        <FormControl required>
                            <FormLabel>Code from Telegram</FormLabel>
                            <Input
                                startDecorator={<KeyIcon />}
                                placeholder="12345"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                inputMode="numeric"
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Password (2FA, if enabled)</FormLabel>
                            <Input
                                startDecorator={<LockIcon />}
                                placeholder="Telegram cloud password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                            />
                        </FormControl>
                        <Stack direction="row" spacing={1}>
                            <Button variant="soft" color="neutral" onClick={handleBackToPhone}>
                                Back
                            </Button>
                            <Button variant="solid" color="success" onClick={handleSignIn} disabled={!canSignIn} loading={signingIn} sx={{ ml: 'auto' }}>
                                Sign in
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </ModalDialog>
        </Modal>
    )
}
